import { useCallback } from 'react'

import { FieldApi } from '@tanstack/react-form'
import { FormInstance } from 'antd'
import {
  GovWellFormFieldClassName,
  GovwellFormFieldErrorClassName,
} from 'govwell-ui/components/FormControl/util'
import isNil from 'lodash.isnil'
import { FieldError } from 'rc-field-form/lib/interface'
import { DateTemplateInput } from 'types/graphql'

import { useNotification } from 'src/components/shared/Notification/NotificationContext'

export const useAnnounceFirstInvalidField = (form?: FormInstance) => {
  const { notification } = useNotification()

  return useCallback(() => {
    const firstInvalidFieldId = form
      ?.getFieldsError()
      .find((field: FieldError) => !!field.errors.length)
      ?.name?.join('_')

    let firstInvalidField: HTMLElement | null | undefined
    if (firstInvalidFieldId) {
      // Handles antd form invalid fields
      firstInvalidField = document.getElementById(`${firstInvalidFieldId}`)
    } else {
      // Handles govwell-ui / tanstack form invalid fields
      firstInvalidField = document
        .querySelector(`.${GovwellFormFieldErrorClassName}`)
        ?.closest(`.${GovWellFormFieldClassName}`)
    }

    if (!firstInvalidField) {
      return
    }

    firstInvalidField.style.scrollMargin = '40px'
    firstInvalidField.scrollIntoView({
      behavior: 'smooth',
    })
    notification.error({
      message: 'One or more fields is invalid.',
    })
  }, [form, notification])
}

export const validators = {
  dateTemplateInput: (value: Partial<DateTemplateInput>): string | undefined => {
    if (
      value.type === 'Interval' &&
      (isNil(value.interval?.count) || isNil(value.interval?.type))
    ) {
      return 'Interval is missing one or more fields'
    }
  },
}

export interface TanstackField<TData, TKey extends string>
  extends FieldApi<
    any,
    TKey,
    TData,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any,
    any
  > {}
export type SelectFieldProps<TData, TKey extends string> =
  | {
      isMultiSelect: true
      field: TanstackField<TData[], TKey>
    }
  | {
      isMultiSelect: false
      field: TanstackField<TData | null | undefined, TKey>
    }

export const EmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
export const PhoneNumberRegex = new RegExp(/^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/)
export const ZipCodeRegex = new RegExp(/^\d{5}(-\d{4})?$/)

export const getRequiredValidator = (message: string) => ({
  onBlur({ value }: { value: unknown }) {
    if (!value) {
      return message
    }
  },
})

export const getEmailValidator = (args?: { isRequired?: boolean }) => {
  const { isRequired = true } = args ?? {}

  return {
    onBlur({ value }: { value: string | string[] }) {
      if (!value) {
        return isRequired ? 'Please enter an email address' : undefined
      }
      const values = Array.isArray(value) ? value : [value]
      if (values.some((email) => !EmailRegex.test(email))) {
        return 'Please enter a valid email address'
      }
    },
  }
}

export const getPhoneNumberValidator = (args?: { isRequired?: boolean }) => {
  const { isRequired = true } = args ?? {}

  return {
    onBlur({ value }: { value: string }) {
      if (!value) {
        return isRequired ? 'Please enter a phone number' : undefined
      }
      if (!PhoneNumberRegex.test(value)) {
        return 'Please enter a valid phone number'
      }
    },
  }
}

export const getUrlValidator = (args?: { isRequired?: boolean }) => {
  const { isRequired = true } = args ?? {}

  return {
    onBlur({ value }: { value: string }) {
      if (!value) {
        return isRequired ? 'Please enter a URL' : undefined
      }
      if (!URL.canParse(value)) {
        return 'Please enter a valid URL'
      }
    },
  }
}
